import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  Button,
  Alert,
  Chip,
  TextField as MuiTextField,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getCampaignStatus,
  setCampaignDates,
  clearCampaignDates,
  CampaignStatus,
  SetCampaignDatesInput,
  getPostCampaignCleanupStats,
  setCleanupScheduledTime,
  clearCleanupScheduledTime,
  PostCampaignCleanupStats,
} from '../../../services/redpacketService';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';

const CampaignDatesList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupStats, setCleanupStats] = useState<PostCampaignCleanupStats | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [reason, setReason] = useState('');
  const [cleanupScheduledTime, setCleanupScheduledTimeValue] = useState<Dayjs | null>(null);
  const [cleanupReason, setCleanupReason] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const status = await getCampaignStatus();
      setCampaignStatus(status);
      // 如果有日期，设置到日期选择器
      if (status.startDate) {
        setStartDate(dayjs(status.startDate));
      }
      if (status.endDate) {
        setEndDate(dayjs(status.endDate));
      }
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.campaignStatusFetchFailed'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify,translate]);

  const fetchCleanupStats = useCallback(async () => {
    try {
      setCleanupLoading(true);
      const stats = await getPostCampaignCleanupStats();
      setCleanupStats(stats);
      if (stats?.scheduledTime) {
        setCleanupScheduledTimeValue(dayjs(stats.scheduledTime));
      } else {
        setCleanupScheduledTimeValue(null);
      }
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.postCampaignCleanupStatsFetchFailed'), {
        type: 'error',
      });
    } finally {
      setCleanupLoading(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    fetchCleanupStats();
  }, [fetchCleanupStats]);

  const handleSetDates = async () => {
    try {
      setLoading(true);
      const input: SetCampaignDatesInput = {
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
        reason: reason || undefined,
      };
      const result = await setCampaignDates(input);
      setCampaignStatus(result);
      notify(translate('module.redpacket.campaignDatesUpdatedSuccess'), { type: 'success' });
      setReason('');
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.campaignDatesUpdateFailed'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClearDates = async () => {
    if (!window.confirm(translate('module.redpacket.confirmClearCampaignDates'))) {
      return;
    }
    try {
      setLoading(true);
      const result = await clearCampaignDates(reason || undefined);
      setCampaignStatus(result);
      setStartDate(null);
      setEndDate(null);
      setReason('');
      notify(translate('module.redpacket.campaignDatesClearedSuccess'), { type: 'success' });
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.campaignDatesClearFailed'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetCleanupScheduledTime = async () => {
    if (!cleanupScheduledTime) {
      notify(translate('module.redpacket.cleanupScheduledTimeRequired'), { type: 'warning' });
      return;
    }
    if (!cleanupReason) {
      notify(translate('module.redpacket.cleanupReasonRequired'), { type: 'warning' });
      return;
    }
    try {
      setCleanupLoading(true);
      const result = await setCleanupScheduledTime(cleanupScheduledTime.toISOString(), cleanupReason);
      setCleanupStats(result);
      notify(translate('module.redpacket.cleanupScheduledTimeSetSuccess'), { type: 'success' });
      setCleanupReason('');
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.cleanupScheduledTimeSetFailed'), {
        type: 'error',
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleClearCleanupScheduledTime = async () => {
    if (!window.confirm(translate('module.redpacket.confirmClearCleanupScheduledTime'))) {
      return;
    }
    if (!cleanupReason) {
      notify(translate('module.redpacket.cleanupReasonRequired'), { type: 'warning' });
      return;
    }
    try {
      setCleanupLoading(true);
      const result = await clearCleanupScheduledTime(cleanupReason);
      setCleanupStats(result);
      setCleanupScheduledTimeValue(null);
      notify(translate('module.redpacket.cleanupScheduledTimeClearedSuccess'), { type: 'success' });
      setCleanupReason('');
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.cleanupScheduledTimeClearFailed'), {
        type: 'error',
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.campaignDates')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.campaignDates') },
          ]}
        />

        <Card sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            {translate('module.redpacket.campaignStatus')}
          </Typography>

          {campaignStatus && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('module.redpacket.status')}
                  </Typography>
                  <Chip
                    label={campaignStatus.isActive ? translate('resource.active') : translate('resource.inactive')}
                    color={campaignStatus.isActive ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('module.redpacket.statusMessage')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {campaignStatus.statusMessage}
                  </Typography>
                </Grid>
                {campaignStatus.startDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {translate('module.redpacket.startDate')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {dayjs(campaignStatus.startDate).format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                    {campaignStatus.startTimestamp && (
                      <Typography variant="caption" color="text.secondary">
                        Timestamp: {campaignStatus.startTimestamp}
                      </Typography>
                    )}
                  </Grid>
                )}
                {campaignStatus.endDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {translate('module.redpacket.endDate')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {dayjs(campaignStatus.endDate).format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                    {campaignStatus.endTimestamp && (
                      <Typography variant="caption" color="text.secondary">
                        Timestamp: {campaignStatus.endTimestamp}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
            {translate('module.redpacket.setCampaignDates')}
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label={translate('module.redpacket.startDate')}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label={translate('module.redpacket.endDate')}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  minDateTime={startDate || undefined}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  fullWidth
                  label={translate('module.redpacket.changeReasonBtn')}
                  placeholder={translate('module.redpacket.changeReasonPlaceholder')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={loading}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <PermissionButton resource="redpacket" action="update">
                    <Button
                      variant="contained"
                      onClick={handleSetDates}
                      disabled={loading}
                      size="large"
                    >
                      {loading ? translate('module.redpacket.updating') : translate('module.redpacket.setCampaignDates')}
                    </Button>
                  </PermissionButton>
                  <PermissionButton resource="redpacket" action="update">
                    <Button
                      variant="outlined"
                      onClick={handleClearDates}
                      disabled={loading}
                      color="error"
                    >
                      {translate('module.redpacket.clearCampaignDates')}
                    </Button>
                  </PermissionButton>
                </Stack>
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              {translate('module.redpacket.campaignNote')}
            </Typography>
          </Alert>
        </Card>

        <Card sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            {translate('module.redpacket.postCampaignCleanupConfig')}
          </Typography>

          {cleanupStats && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('module.redpacket.cleanupTriggered')}
                  </Typography>
                  <Chip
                    label={
                      cleanupStats.cleanupTriggered
                        ? translate('module.redpacket.cleanupTriggeredYes')
                        : translate('module.redpacket.cleanupTriggeredNo')
                    }
                    color={cleanupStats.cleanupTriggered ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translate('module.redpacket.scheduledTime')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {cleanupStats.scheduledTime
                      ? dayjs(cleanupStats.scheduledTime).format('YYYY-MM-DD HH:mm:ss')
                      : '-'}
                  </Typography>
                  {cleanupStats.scheduledTime && (
                    <Typography variant="caption" color="text.secondary">
                      Timestamp: {cleanupStats.scheduledTime}
                    </Typography>
                  )}
                </Grid>

                {cleanupStats.latestRun && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 1 }}>
                        {translate('module.redpacket.latestCleanupRun')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.status')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.status || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.cutoffTime')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.cutoffTime
                          ? dayjs(cleanupStats.latestRun.cutoffTime).format('YYYY-MM-DD HH:mm:ss')
                          : '-'}
                      </Typography>
                      {cleanupStats.latestRun.cutoffTime && (
                        <Typography variant="caption" color="text.secondary">
                          Timestamp: {cleanupStats.latestRun.cutoffTime}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.runId')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.id || '-'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.expiredPacketsCount')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.expiredPacketsCount ?? 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.expiredPacketsAmount')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.expiredPacketsAmount ?? '0'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.smallBalanceUsersCount')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.smallBalanceUsersCount ?? 0}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.smallBalanceRecoveredAmount')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.smallBalanceRecoveredAmount ?? '0'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.autoClaimUsersCount')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.autoClaimUsersCount ?? 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.autoClaimAmount')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.autoClaimAmount ?? '0'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.autoClaimFailedCount')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.autoClaimFailedCount ?? 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.sharedPoolRecovered')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.sharedPoolRecovered ?? '0'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.criticalHitPoolRecovered')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.criticalHitPoolRecovered ?? '0'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.designatedPoolRecovered')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.designatedPoolRecovered ?? '0'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.startedAt')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.startedAt
                          ? dayjs(cleanupStats.latestRun.startedAt).format('YYYY-MM-DD HH:mm:ss')
                          : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {translate('module.redpacket.completedAt')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {cleanupStats.latestRun.completedAt
                          ? dayjs(cleanupStats.latestRun.completedAt).format('YYYY-MM-DD HH:mm:ss')
                          : '-'}
                      </Typography>
                    </Grid>

                    {cleanupStats.latestRun.errorMessage && (
                      <Grid item xs={12}>
                        <Alert severity="error">
                          <Typography variant="body2">
                            {cleanupStats.latestRun.errorMessage}
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          )}

          <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
            {translate('module.redpacket.configureCleanupScheduledTime')}
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label={translate('module.redpacket.scheduledTime')}
                  value={cleanupScheduledTime}
                  onChange={(newValue) => setCleanupScheduledTimeValue(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MuiTextField
                  fullWidth
                  label={translate('module.redpacket.changeReasonBtn')}
                  placeholder={translate('module.redpacket.changeReasonPlaceholder')}
                  value={cleanupReason}
                  onChange={(e) => setCleanupReason(e.target.value)}
                  disabled={cleanupLoading}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <PermissionButton resource="redpacket" action="update">
                    <Button
                      variant="contained"
                      onClick={handleSetCleanupScheduledTime}
                      disabled={cleanupLoading}
                      size="large"
                    >
                      {cleanupLoading
                        ? translate('module.redpacket.updating')
                        : translate('module.redpacket.setCleanupScheduledTime')}
                    </Button>
                  </PermissionButton>
                  <PermissionButton resource="redpacket" action="update">
                    <Button
                      variant="outlined"
                      onClick={handleClearCleanupScheduledTime}
                      disabled={cleanupLoading}
                      color="error"
                    >
                      {translate('module.redpacket.clearCleanupScheduledTime')}
                    </Button>
                  </PermissionButton>
                  <Button variant="text" onClick={fetchCleanupStats} disabled={cleanupLoading}>
                    {translate('module.redpacket.refresh')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              {translate('module.redpacket.cleanupScheduleNote')}
            </Typography>
          </Alert>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default CampaignDatesList;
